const $$splitComponentImporter = () => import('/app/apps/web/src/routes/_app/release/$id.tsx?tsr-split=component');
import { lazyRouteComponent } from '@tanstack/react-router';
const $$splitLoaderImporter = () => import('/app/apps/web/src/routes/_app/release/$id.tsx?tsr-split=loader');
import { lazyFn } from '@tanstack/react-router';
import { createFileRoute } from "@tanstack/react-router";
import { createTranslator } from "use-intl";
import { toAbsoluteUrl } from "@/lib/absolute-url";
import { resolveCatalogEntitySeo } from "@/lib/catalog-seo-policy";
import { jsonLdScripts } from "@/lib/jsonLdScripts";
import { withSiteTitle } from "@/lib/page-title";
import { pickString } from "@/lib/search-params";
import { buildSeoMetaTags } from "@/lib/seo-meta";
import { getRootLoaderData } from "@/types/intl";
import { getPrimaryFormatCategory } from "@garimpa/lib/formatClassification";
type ReleaseSearch = {
  playing?: string;
};
interface ReleaseBreadcrumbInput {
  title: string;
  workId: string | null;
  workTitle: string | null;
  primaryIndex: {
    label: string;
    path: string;
  } | null;
  artists: Array<{
    id: string;
    name: string;
  }>;
}
function buildReleaseBreadcrumbItems(release: ReleaseBreadcrumbInput, releaseId: string) {
  const items: Array<{
    name: string;
    url: string;
  }> = [];
  const primaryIndex = release.primaryIndex ? {
    name: release.primaryIndex.label,
    url: release.primaryIndex.path
  } : null;
  const currentRelease = {
    name: release.title,
    url: `/release/${releaseId}`
  };
  const artist = release.artists[0] ? {
    name: release.artists[0].name,
    url: `/artist/${release.artists[0].id}`
  } : null;
  if (primaryIndex) items.push(primaryIndex);
  if (artist) items.push(artist);
  if (release.workId && release.workTitle) {
    items.push({
      name: release.workTitle,
      url: `/work/${release.workId}`
    });
  }
  items.push(currentRelease);
  return items;
}
export const Route = createFileRoute("/_app/release/$id")({
  validateSearch: (raw: Record<string, unknown>): ReleaseSearch => {
    const pickRaw = (key: string) => raw[key] as string | string[] | undefined;
    return {
      playing: pickString(pickRaw("playing"))
    };
  },
  // Search params on this route are UI-only (`playing` toggles the inline
  // player); the loader reads nothing from `search`, so deps are intentionally
  // empty — no loader re-run / prefetch reset on `?playing=` changes.
  loaderDeps: () => ({}),
  loader: lazyFn($$splitLoaderImporter, 'loader'),
  head: async ({
    match,
    loaderData,
    params
  }) => {
    const {
      locale,
      messages
    } = getRootLoaderData(match);
    const tMeta = createTranslator({
      locale,
      messages,
      namespace: "globals.metadata"
    });
    const tSeo = createTranslator({
      locale,
      messages,
      namespace: "globals.seo"
    });
    const tGenres = createTranslator({
      locale,
      messages,
      namespace: "shared.genres"
    });
    const tStyles = createTranslator({
      locale,
      messages,
      namespace: "shared.styles"
    });
    const release = loaderData?.release;
    if (!release) {
      return {
        meta: [{
          title: withSiteTitle(tMeta("release"))
        }]
      };
    }
    const {
      buildReleaseSeoDescription,
      buildReleaseSeoFormat,
      buildReleaseSeoTitle,
      extractReleaseSeoYear,
      formatSeoCountryList,
      formatSeoTaxonomyList
    } = await import("@/lib/seo");
    const {
      buildCatalogPageJsonLd,
      buildReleaseJsonLd
    } = await import("@/utils/jsonld");
    const entitySeo = resolveCatalogEntitySeo({
      policy: release.seo,
      currentEntityType: "release",
      currentCatalogId: release.id,
      locale
    });
    const seoFormatSummary = buildReleaseSeoFormat({
      formatCategories: release.mediaCategories,
      tSeo,
      locale
    });
    const primaryFormatCategory = getPrimaryFormatCategory({
      mediaCategories: release.mediaCategories
    });
    const primarySeoFormat = buildReleaseSeoFormat({
      formatCategories: primaryFormatCategory ? [primaryFormatCategory] : [],
      tSeo,
      locale
    });
    const title = buildReleaseSeoTitle({
      title: release.title,
      artist: release.artistDisplay,
      formatSummary: seoFormatSummary,
      primaryFormat: primarySeoFormat,
      releasedAt: extractReleaseSeoYear(release.date),
      tSeo
    });
    const country = formatSeoCountryList(release.country, locale);
    const labelName = release.imprints[0]?.imprintName;
    const tags = formatSeoTaxonomyList({
      genres: release.genres,
      styles: release.styles,
      translators: {
        genres: tGenres,
        styles: tStyles
      },
      locale
    });
    const description = buildReleaseSeoDescription({
      title: release.title,
      artist: release.artistDisplay,
      formatSummary: seoFormatSummary,
      hasOffers: Boolean(loaderData.seoOffer),
      releasedAt: extractReleaseSeoYear(release.date),
      country,
      label: labelName ?? null,
      tags,
      tSeo,
      locale
    });
    const imageUrl = toAbsoluteUrl(release.imageUrl) ?? undefined;
    const url = entitySeo.canonicalUrl;
    const jsonLd = buildCatalogPageJsonLd({
      entity: release,
      emitStructuredData: entitySeo.emitStructuredData,
      locale,
      buildEntityJsonLd: release => buildReleaseJsonLd(release, description, loaderData.seoOffer),
      buildBreadcrumbItems: release => buildReleaseBreadcrumbItems(release, params.id)
    });
    return {
      meta: buildSeoMetaTags({
        title,
        description,
        canonicalUrl: url,
        imageUrl
      }, {
        robots: entitySeo.robots,
        openGraph: {
          type: "music.album"
        },
        twitter: {}
      }),
      links: entitySeo.links,
      scripts: jsonLdScripts(jsonLd?.entity, jsonLd?.breadcrumb)
    };
  },
  component: lazyRouteComponent($$splitComponentImporter, 'component')
});